export type ProductionStatementApprovalReferenceFormatItemKind =
  "digit" | "letter" | "uppercase" | "lowercase" | "symbol" | "fixed" | "exact"

export interface ProductionStatementApprovalReferenceFormatItem {
  kind: ProductionStatementApprovalReferenceFormatItemKind
  value?: string
}

export interface ProductionStatementApprovalReferenceFormatPosition {
  placeholder: string
  fixed?: boolean
  matches: (character: string) => boolean
}

const STRUCTURED_FORMAT_PREFIX = "p1:"

export function normalizeProductionStatementApprovalReferenceFormat(
  value: string | null | undefined
): string | null {
  return value?.trim() || null
}

export function parseProductionStatementApprovalReferenceFormat(
  value: string | null | undefined
): ProductionStatementApprovalReferenceFormatItem[] {
  const normalizedFormat = normalizeProductionStatementApprovalReferenceFormat(value)
  if (!normalizedFormat) return []

  if (!normalizedFormat.startsWith(STRUCTURED_FORMAT_PREFIX)) {
    return [...normalizedFormat].map((character) =>
      /[0-9]/.test(character)
        ? { kind: "digit" }
        : {
            kind: "fixed",
            value: character
          }
    )
  }

  const encodedItems = normalizedFormat.slice(STRUCTURED_FORMAT_PREFIX.length)
  if (!encodedItems) return []

  try {
    return encodedItems.split(",").map((encodedItem) => {
      if (encodedItem === "d") return { kind: "digit" }
      if (encodedItem === "l") return { kind: "letter" }
      if (encodedItem === "lu") return { kind: "uppercase" }
      if (encodedItem === "ll") return { kind: "lowercase" }
      if (encodedItem === "s") return { kind: "symbol" }
      if (encodedItem.startsWith("f:") || encodedItem.startsWith("e:")) {
        const value = decodeURIComponent(encodedItem.slice(2))
        if (!value) throw new Error("Lege vaste waarde")
        return {
          kind: encodedItem.startsWith("e:") ? "exact" : "fixed",
          value
        }
      }
      throw new Error("Onbekend formatonderdeel")
    })
  } catch {
    return []
  }
}

export function serializeProductionStatementApprovalReferenceFormat(
  items: ProductionStatementApprovalReferenceFormatItem[]
): string | null {
  if (!items.length) return null

  const encodedItems = items.map((item) => {
    if (item.kind === "digit") return "d"
    if (item.kind === "letter") return "l"
    if (item.kind === "uppercase") return "lu"
    if (item.kind === "lowercase") return "ll"
    if (item.kind === "symbol") return "s"
    return `${item.kind === "exact" ? "e" : "f"}:${encodeURIComponent(item.value || "")}`
  })

  return `${STRUCTURED_FORMAT_PREFIX}${encodedItems.join(",")}`
}

export function productionStatementApprovalReferenceFormatExample(
  format: string | null | undefined
): string | null {
  const normalizedFormat = normalizeProductionStatementApprovalReferenceFormat(format)
  if (!normalizedFormat) return null

  const positions = productionStatementApprovalReferenceFormatPositions(normalizedFormat)
  if (!positions.length) return null

  return positions.map((position) => position.placeholder).join("")
}

export function productionStatementApprovalReferenceFormatPositions(
  format: string | null | undefined
): ProductionStatementApprovalReferenceFormatPosition[] {
  const normalizedFormat = normalizeProductionStatementApprovalReferenceFormat(format)
  if (!normalizedFormat) return []
  if (!normalizedFormat.startsWith(STRUCTURED_FORMAT_PREFIX)) {
    return [...normalizedFormat].map((character) => ({
      placeholder: character,
      fixed: !/[0-9]/.test(character),
      matches: /[0-9]/.test(character)
        ? (actualCharacter: string) => /[0-9]/.test(actualCharacter)
        : (actualCharacter: string) =>
            actualCharacter.toLocaleLowerCase("nl") === character.toLocaleLowerCase("nl")
    }))
  }

  const items = parseProductionStatementApprovalReferenceFormat(normalizedFormat)

  return items.flatMap((item) => {
    if (item.kind === "digit") {
      return [{ placeholder: "0", matches: (character: string) => /[0-9]/.test(character) }]
    }
    if (item.kind === "letter") {
      return [{ placeholder: "A", matches: (character: string) => /[A-Za-z]/.test(character) }]
    }
    if (item.kind === "uppercase") {
      return [{ placeholder: "A", matches: (character: string) => /^[A-Z]$/.test(character) }]
    }
    if (item.kind === "lowercase") {
      return [{ placeholder: "a", matches: (character: string) => /^[a-z]$/.test(character) }]
    }
    if (item.kind === "symbol") {
      return [{ placeholder: "-", matches: (character: string) => /[^A-Za-z0-9]/.test(character) }]
    }
    return [...(item.value || "")].map((fixedCharacter) => ({
      placeholder: fixedCharacter,
      fixed: true,
      matches: (character: string) =>
        item.kind === "exact"
          ? character === fixedCharacter
          : character.toLocaleLowerCase("nl") === fixedCharacter.toLocaleLowerCase("nl")
    }))
  })
}

export function productionStatementApprovalReferenceCharacterMatches(
  reference: string | null | undefined,
  format: string | null | undefined
): boolean[] {
  const positions = productionStatementApprovalReferenceFormatPositions(format)
  return [...(reference || "")].map(
    (character, index) => positions[index]?.matches(character) ?? false
  )
}

export function productionStatementApprovalReferenceMatchesFormat(
  reference: string | null | undefined,
  format: string | null | undefined
): boolean {
  const normalizedFormat = normalizeProductionStatementApprovalReferenceFormat(format)
  const normalizedReference = reference?.trim() || ""
  if (!normalizedFormat || !normalizedReference) return true

  const positions = productionStatementApprovalReferenceFormatPositions(normalizedFormat)
  const actualCharacters = [...normalizedReference]
  return (
    positions.length === actualCharacters.length &&
    productionStatementApprovalReferenceCharacterMatches(
      normalizedReference,
      normalizedFormat
    ).every(Boolean)
  )
}

export function productionStatementApprovalReferenceFormatError(
  format: string | null | undefined
): string {
  return `Gebruik het verwachte format, bijvoorbeeld "${productionStatementApprovalReferenceFormatExample(format)}".`
}
